import { Title, Textarea, Text, Modal, Button, Group } from "@mantine/core"
import { MantineReactTable } from "mantine-react-table"
import { useState } from "react"

import { OrganizationKnowledgeResponses } from "@costory/types/endpoints/organizationKnowledge"

import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import {
  useCreateOrganizationKnowledge,
  useOrganizationKnowledge,
} from "@costory/front/queries/organizationKnowledge"
import { truncateLabel } from "@costory/front/utils/charts"

export const OrganizationKnowledgePage = () => {
  const query = useOrganizationKnowledge()
  return (
    <QueryWrapper query={query} allowEmptyArray>
      {({ data }) => (
        <_OrganizationKnowledgePage organizationKnowledge={data} />
      )}
    </QueryWrapper>
  )
}

const _OrganizationKnowledgePage = ({
  organizationKnowledge,
}: {
  organizationKnowledge: OrganizationKnowledgeResponses.OrganizationKnowledge[]
}) => {
  const [selectedKnowledge, setSelectedKnowledge] =
    useState<OrganizationKnowledgeResponses.OrganizationKnowledge | null>(null)

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [newContent, setNewContent] = useState("")

  const getLatestContent = () => {
    if (!organizationKnowledge || organizationKnowledge.length === 0) return ""
    const latest = organizationKnowledge.reduce(
      (acc, curr) =>
        new Date(curr.createdAt) > new Date(acc.createdAt) ? curr : acc,
      organizationKnowledge[0],
    )
    return latest.content
  }

  const { mutateAsync: createKnowledge, isPending } =
    useCreateOrganizationKnowledge()

  const openCreate = () => {
    setNewContent(getLatestContent())
    setIsCreateOpen(true)
  }

  const onCreate = async () => {
    await createKnowledge({ content: newContent })
    setIsCreateOpen(false)
    setNewContent("")
  }

  return (
    <>
      <Group justify="space-between" mb="md">
        <Title order={2}>Organization Knowledge</Title>
        <Button onClick={openCreate}>Create new</Button>
      </Group>

      <MantineReactTable
        columns={[
          {
            accessorKey: "content",
            header: "Content",
            Cell: ({ cell }) => {
              return (
                <Text onClick={() => setSelectedKnowledge(cell.row.original)}>
                  {truncateLabel(cell.getValue<string>(), 100)}...
                </Text>
              )
            },
          },
          {
            accessorKey: "createdAt",
            header: "Created At",
            Cell: ({ cell }) =>
              new Date(cell.getValue<Date>()).toLocaleString(),
          },
        ]}
        data={organizationKnowledge}
        enableRowSelection
        mantineTableProps={{
          highlightOnHover: true,
        }}
      />

      <Modal
        opened={!!selectedKnowledge}
        onClose={() => setSelectedKnowledge(null)}
        title="Selected Knowledge"
        size="lg"
      >
        {selectedKnowledge && (
          <Textarea
            value={selectedKnowledge.content}
            readOnly
            minRows={5}
            autosize
          />
        )}
      </Modal>

      <Modal
        opened={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Create Organization Knowledge"
        size="lg"
      >
        <Textarea
          value={newContent}
          onChange={(e) => setNewContent(e.currentTarget.value)}
          minRows={8}
          autosize
          placeholder="Enter knowledge content"
        />
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setIsCreateOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={onCreate}
            loading={isPending}
            disabled={!newContent.trim()}
          >
            Save
          </Button>
        </Group>
      </Modal>
    </>
  )
}
